import React, { createContext, useCallback, useContext, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, Loader2, X } from 'lucide-react';
import clsx from 'clsx';
import { useTheme } from '../../contexts/ThemeContext.tsx';
import { atualizarLead, formatBrazilianPhone, formatBrazilianPhoneInput, obterLead, validateBrazilianWhatsAppPhone, type Lead, type ParsedApiError } from '../../services/api.ts';
import { getPhoneCorrectionError, type CrmSaleFailure } from './crmPhoneFeedback.ts';

export const phonePrimaryButton = 'inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-sminy-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-sminy-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sminy-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';
const phoneSecondaryButton = 'inline-flex min-h-11 items-center justify-center rounded-xl border px-4 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sminy-primary disabled:cursor-not-allowed disabled:opacity-50';

interface DialogProps {
  title: string;
  description?: string;
  busy?: boolean;
  onClose: () => void;
  children: React.ReactNode;
  footer: React.ReactNode;
  returnFocus?: React.RefObject<HTMLElement | null>;
}

/** Dialog local da correção de telefone; não altera os outros modais do CRM. */
export function CrmPhoneDialog({ title, description, busy, onClose, children, footer, returnFocus }: DialogProps) {
  const { isDark } = useTheme();
  const titleId = useId();
  const descriptionId = useId();
  const panel = useRef<HTMLElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const previous = returnFocus?.current || document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panel.current?.focus();
    return () => {
      document.body.style.overflow = overflow;
      if (previous?.isConnected) previous.focus();
    };
  }, [returnFocus]);
  const keyboard = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      if (!busy) closeRef.current();
    }
    if (event.key === 'Tab') {
      const items = Array.from(panel.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), a[href], [tabindex="0"]') || []);
      const first = items[0]; const last = items[items.length - 1];
      if (!items.length) { event.preventDefault(); return; }
      if (event.shiftKey && (document.activeElement === first || document.activeElement === panel.current)) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || document.activeElement === panel.current)) { event.preventDefault(); first.focus(); }
    }
  };
  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 px-4 py-[max(1rem,env(safe-area-inset-top),env(safe-area-inset-bottom))]" onKeyDown={keyboard}
      onMouseDown={event => { if (event.target === event.currentTarget && !busy) onClose(); }}>
      <section ref={panel} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={description ? descriptionId : undefined} aria-busy={busy}
        className={clsx('flex max-h-[calc(100dvh-2rem)] w-full max-w-[560px] flex-col overflow-hidden rounded-2xl border p-6 shadow-xl outline-none', isDark ? 'border-sminy-dark-border bg-sminy-dark-elevated text-sminy-dark-text-primary' : 'border-sminy-border bg-white text-sminy-text-primary')}>
        <header className="mb-5 flex shrink-0 items-start justify-between gap-3">
          <div className="min-w-0"><h2 id={titleId} className="text-xl font-semibold leading-7">{title}</h2>
            {description && <p id={descriptionId} className={clsx('mt-2 text-sm leading-6', isDark ? 'text-sminy-dark-text-secondary' : 'text-sminy-text-secondary')}>{description}</p>}
          </div>
          <button type="button" onClick={onClose} disabled={busy} aria-label="Fechar diálogo" className="grid min-h-11 min-w-11 shrink-0 place-items-center rounded-xl hover:bg-sminy-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sminy-primary disabled:opacity-50"><X className="h-5 w-5" /></button>
        </header>
        <div className="min-h-0 overflow-y-auto overscroll-contain text-sm leading-6">{children}</div>
        <footer className="mt-5 flex shrink-0 flex-col gap-2 sm:flex-row sm:justify-end [&>button]:w-full sm:[&>button]:w-auto">{footer}</footer>
      </section>
    </div>, document.body,
  );
}

type PhoneTarget = { leadId: number; name?: string };
const PhoneContext = createContext<{ enabled: boolean; edit: (target: PhoneTarget) => void }>({ enabled: false, edit: () => {} });
export const useCrmPhoneCorrection = () => useContext(PhoneContext);

export function CrmPhoneCorrectionProvider({ getAuth, onSaved, onOpenChange, children }: {
  getAuth: () => { effectiveClientId: number; clinicId: number };
  onSaved: (lead: Lead) => void;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}) {
  const { isDark } = useTheme();
  const [target, setTarget] = useState<PhoneTarget | null>(null);
  const [lead, setLead] = useState<Lead | null>(null);
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ParsedApiError | null>(null);
  const request = useRef(0);
  const savingRef = useRef(false);
  const input = useRef<HTMLInputElement>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const callbacks = useRef({ onSaved, onOpenChange });
  callbacks.current = { onSaved, onOpenChange };
  useEffect(() => () => { request.current += 1; }, []);
  const close = useCallback(() => {
    if (savingRef.current) return;
    request.current += 1;
    setTarget(null); setLead(null); setError(null);
    callbacks.current.onOpenChange(false);
  }, []);
  const load = useCallback(async (selected: PhoneTarget, reconcileSaved = false) => {
    const current = ++request.current;
    setLead(null); setError(null); setLoading(true);
    try {
      const auth = getAuth();
      const loaded = await obterLead(auth.effectiveClientId, auth.clinicId, selected.leadId);
      if (current !== request.current) return;
      if (reconcileSaved) {
        callbacks.current.onSaved(loaded);
        close();
        return;
      }
      setLead(loaded);
      setPhone(loaded.phone ? formatBrazilianPhone(loaded.phone) : '');
    } catch {
      if (current === request.current) setError({ message: 'Não foi possível carregar o cadastro. Tente recarregar os dados.' });
    } finally {
      if (current === request.current) setLoading(false);
    }
  }, [getAuth, close]);
  const edit = useCallback((selected: PhoneTarget) => {
    if (savingRef.current || !Number.isInteger(selected.leadId) || selected.leadId <= 0) return;
    returnFocus.current = document.querySelector<HTMLElement>('button[aria-label="Opções"][aria-expanded="true"]') || document.activeElement as HTMLElement;
    setTarget(selected); setPhone('');
    callbacks.current.onOpenChange(true);
    void load(selected);
  }, [load]);
  useEffect(() => { if (target && lead && !loading) input.current?.focus(); }, [target, lead, loading]);
  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!lead || savingRef.current || loading) return;
    const validation = validateBrazilianWhatsAppPhone(phone);
    if (!validation.isValid) {
      setError({ code: 'invalid_phone', message: 'Informe um telefone válido com DDD.' });
      input.current?.focus(); return;
    }
    savingRef.current = true; setSaving(true); setError(null);
    try {
      const auth = getAuth();
      const updated = await atualizarLead(auth.effectiveClientId, auth.clinicId, lead.id, {
        phone: validation.normalized,
        expected_phone: lead.phone ?? null,
      });
      callbacks.current.onSaved(updated);
      savingRef.current = false;
      close();
    } catch (failure) {
      setError(getPhoneCorrectionError(failure));
    } finally {
      savingRef.current = false; setSaving(false);
    }
  };
  const canReload = !lead || ['phone_correction_stale', 'phone_correction_expected_phone_required', 'lead_update_confirmation_unavailable'].includes(error?.code || '');
  const secondaryClass = clsx(phoneSecondaryButton, isDark ? 'border-sminy-dark-border text-sminy-dark-text-secondary hover:bg-sminy-dark-surface' : 'border-sminy-border text-sminy-text-secondary hover:bg-sminy-canvas');
  const fieldId = useId();
  const helpId = `${fieldId}-help`; const errorId = `${fieldId}-error`;
  return <PhoneContext.Provider value={{ enabled: true, edit }}>
    {children}
    {target && <CrmPhoneDialog title="Editar telefone" description="Corrija o número mantendo o cadastro e o histórico." busy={saving} onClose={close} returnFocus={returnFocus}
      footer={<>
        <button type="button" onClick={close} disabled={saving} className={secondaryClass}>Cancelar</button>
        {canReload ? <button type="button" onClick={() => void load(target, error?.code === 'lead_update_confirmation_unavailable')} disabled={saving || loading} className={phonePrimaryButton}>{loading ? 'Carregando...' : 'Recarregar cadastro'}</button>
          : <button type="submit" form={fieldId} disabled={saving || loading} className={phonePrimaryButton}>{saving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}{saving ? 'Salvando e verificando...' : 'Salvar telefone'}</button>}
      </>}>
      <form id={fieldId} onSubmit={save} className="space-y-5">
        {loading ? <p role="status" className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />Carregando cadastro...</p> : lead && <>
          <div>
            <p className="break-words font-semibold">{lead.name || target.name || 'Cadastro selecionado'}</p>
            <p className={clsx('mt-1 break-words text-xs', isDark ? 'text-sminy-dark-muted' : 'text-sminy-muted')}>Telefone atual: {lead.phone || 'Não informado'}</p>
          </div>
          <div><label htmlFor={`${fieldId}-phone`} className="mb-2 block font-medium">Novo telefone</label>
            <input ref={input} id={`${fieldId}-phone`} type="tel" inputMode="tel" autoComplete="tel-national" placeholder="(11) 98765-4321" value={phone} disabled={saving} aria-invalid={!!error} aria-describedby={`${helpId}${error ? ` ${errorId}` : ''}`}
              onChange={event => {
                const raw = event.target.value;
                const digits = raw.replace(/\D/g, '');
                const withCountryCode = /^\s*\+55/.test(raw) || (digits.startsWith('55') && digits.length > 11);
                // Excedentes permanecem inválidos; a máscara não decide qual dígito excluir.
                setPhone((withCountryCode ? digits.slice(2) : digits).length > 11 ? raw : formatBrazilianPhoneInput(raw));
                setError(null);
              }}
              className={clsx('min-h-11 w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sminy-primary disabled:opacity-50', isDark ? 'border-sminy-dark-border bg-sminy-dark-surface text-sminy-dark-text-primary placeholder:text-sminy-dark-muted' : 'border-sminy-border bg-white text-sminy-text-primary placeholder:text-sminy-muted', error && (isDark ? 'border-sminy-dark-danger' : 'border-sminy-danger'))} />
            <p id={helpId} className={clsx('mt-2 text-xs leading-5', isDark ? 'text-sminy-dark-muted' : 'text-sminy-muted')}>Informe o DDD e o número do contato.</p>
          </div>
          <div><p className={isDark ? 'text-sminy-dark-text-secondary' : 'text-sminy-text-secondary'}>Se o WhatsApp da clínica estiver conectado, o número será verificado antes de salvar.</p>
            <p className={clsx('mt-2 text-xs leading-5', isDark ? 'text-sminy-dark-muted' : 'text-sminy-muted')}>O histórico e a etapa deste cadastro serão mantidos.</p>
          </div>
        </>}
        {error && <p id={errorId} role="alert" className={clsx('flex items-start gap-2 rounded-xl border p-3 text-sm', isDark ? 'border-sminy-dark-danger/30 text-sminy-dark-danger' : 'border-sminy-danger/30 text-sminy-danger')}><AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />{error.message}</p>}
      </form>
    </CrmPhoneDialog>}
  </PhoneContext.Provider>;
}

export function CrmSaleErrorDialog({ failure, leadId, name, onReturn }: { failure: CrmSaleFailure; leadId?: number; name?: string; onReturn: () => void }) {
  const { isDark } = useTheme();
  const actions = useCrmPhoneCorrection();
  const canCorrect = failure.canCorrectPhone && actions.enabled && !!leadId;
  return <CrmPhoneDialog title={failure.title} description={failure.description} onClose={onReturn} footer={<>
    <button type="button" onClick={onReturn} className={clsx(phoneSecondaryButton, isDark ? 'border-sminy-dark-border text-sminy-dark-text-secondary' : 'border-sminy-border text-sminy-text-secondary')}>Voltar à venda</button>
    {canCorrect && <button type="button" className={phonePrimaryButton} onClick={() => { actions.edit({ leadId: leadId!, name }); onReturn(); }}>Corrigir telefone</button>}
  </>}>
    <div className="space-y-2"><p role="alert" className={clsx('font-medium', isDark ? 'text-sminy-dark-danger' : 'text-sminy-danger')}>{failure.message}</p><p className={isDark ? 'text-sminy-dark-text-secondary' : 'text-sminy-text-secondary'}>{failure.guidance}</p></div>
  </CrmPhoneDialog>;
}
